<template>
  <div class="page page--fill yousign">
    <div class="page-header">
      <div>
        <h1>Signatures Yousign</h1>
        <p>Par compte : réservations réellement signées via Yousign (procédure terminée) et procédures lancées.</p>
      </div>
    </div>

    <div class="kpis">
      <div class="kpi">
        <span>Réservations signées</span>
        <strong>{{ totals.signed.toLocaleString('fr-FR') }}</strong>
      </div>
      <div class="kpi">
        <span>Procédures lancées</span>
        <strong>{{ totals.launched.toLocaleString('fr-FR') }}</strong>
      </div>
      <div class="kpi">
        <span>Taux de finalisation</span>
        <strong>{{ totals.launched ? Math.round(totals.signed / totals.launched * 100) : 0 }} %</strong>
      </div>
      <div class="kpi">
        <span>Comptes utilisateurs</span>
        <strong>{{ rows.length }}</strong>
      </div>
    </div>

    <div class="filters">
      <v-text-field
        v-model="search"
        placeholder="Rechercher un compte"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-details
        clearable
        style="max-width: 280px" />
      <v-menu
        v-model="menuFrom"
        :close-on-content-click="false"
        offset-y
        min-width="290px">
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="formatDate(dateFrom)"
            label="Du"
            prepend-inner-icon="mdi-calendar-outline"
            readonly
            outlined
            dense
            hide-details
            clearable
            style="max-width: 180px"
            v-bind="attrs"
            v-on="on"
            @click:clear="dateFrom = null; fetch()" />
        </template>
        <v-date-picker
          v-model="dateFrom"
          locale="fr"
          :first-day-of-week="1"
          color="primary"
          no-title
          @input="menuFrom = false; fetch()" />
      </v-menu>
      <v-menu
        v-model="menuTo"
        :close-on-content-click="false"
        offset-y
        min-width="290px">
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="formatDate(dateTo)"
            label="Au"
            prepend-inner-icon="mdi-calendar-outline"
            readonly
            outlined
            dense
            hide-details
            clearable
            style="max-width: 180px"
            v-bind="attrs"
            v-on="on"
            @click:clear="dateTo = null; fetch()" />
        </template>
        <v-date-picker
          v-model="dateTo"
          locale="fr"
          :first-day-of-week="1"
          color="primary"
          no-title
          @input="menuTo = false; fetch()" />
      </v-menu>
    </div>

    <div
      v-if="unavailable"
      class="panel notice">
      <v-icon
        color="warning"
        class="mr-3">
        mdi-information-outline
      </v-icon>
      Cette statistique demande la nouvelle version de l'API, pas encore déployée sur l'environnement {{ $env.label }}.
    </div>
    <div
      v-else
      class="table">
      <v-data-table
        :headers="headers"
        :items="rows"
        :search="search"
        :loading="loading"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1], 'items-per-page-text': 'Lignes par page' }"
        :items-per-page="25"
        sort-by="signed"
        sort-desc
        must-sort
        fixed-header
        item-key="pk"
        loading-text="Chargement…"
        no-data-text="Aucune signature sur la période"
        @click:row="row => $router.push({ name: 'accountVue', params: { id: row.pk } })">
        <template #[`item.name`]="{ item }">
          <span class="cell-strong">{{ item.name }}</span>
        </template>
        <template #[`item.account_type`]="{ item }">
          <span :class="pillClass(ACCOUNT_TYPE, item.account_type)">{{ labelOf(ACCOUNT_TYPE, item.account_type) }}</span>
        </template>
        <template #[`item.signed`]="{ item }">
          <span class="cell-strong">{{ item.signed }}</span>
        </template>
        <template #[`item.rate`]="{ item }">
          <div class="rate">
            <div class="rate__bar">
              <div :style="{ width: item.rate + '%' }" />
            </div>
            <span>{{ item.rate }} %</span>
          </div>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
  import { ACCOUNT_TYPE, pillClass, labelOf } from '../labels'

  export default {
    name: 'YousignStats',
    data () {
      return {
        ACCOUNT_TYPE,
        items: [],
        loading: false,
        unavailable: false,
        search: null,
        dateFrom: null,
        dateTo: null,
        menuFrom: false,
        menuTo: false,
        headers: [
          { text: 'Compte', value: 'name' },
          { text: 'Type', value: 'account_type', width: 170 },
          { text: 'Signées', value: 'signed', align: 'end', width: 110 },
          { text: 'Lancées', value: 'launched', align: 'end', width: 110 },
          { text: 'Taux de finalisation', value: 'rate', width: 200 },
          { text: 'Réservations du compte', value: 'reservations', align: 'end', width: 190 }
        ]
      }
    },
    computed: {
      rows () {
        return this.items.map(item => ({
          ...item,
          rate: item.launched ? Math.round(item.signed / item.launched * 100) : 0
        }))
      },
      totals () {
        // une réservation peut compter pour 2 comptes (vendeur + promoteur) : on additionne côté promoteurs
        const promoteurs = this.items.filter(i => i.account_type === 'promoteur')
        return {
          signed: promoteurs.reduce((sum, i) => sum + i.signed, 0),
          launched: promoteurs.reduce((sum, i) => sum + i.launched, 0)
        }
      }
    },
    created () {
      this.fetch()
    },
    methods: {
      pillClass,
      labelOf,
      formatDate (value) {
        return value ? value.split('-').reverse().join('/') : ''
      },
      async fetch () {
        this.loading = true
        this.unavailable = false
        const params = {}
        if (this.dateFrom) params.date_from = this.dateFrom
        if (this.dateTo) params.date_to = this.dateTo
        try {
          const { data } = await this.$api.axios.get('admin/stats/yousign', { params })
          this.items = data
        } catch (e) {
          if (e.response && e.response.status === 404) {
            this.unavailable = true
          } else {
            this.$awn.alert('Impossible de charger la statistique')
          }
        } finally {
          this.loading = false
        }
      }
    }
  }
</script>

<style scoped>
  .kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
    margin-bottom: 20px;
  }
  .kpi {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px 18px;
    background: #fafafa;
    border-radius: 16px;
  }
  .kpi span {
    color: #71717a;
    font-size: 13px;
  }
  .kpi strong {
    font-size: 24px;
    font-weight: 700;
  }
  .filters {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;
  }
  .table {
    background: white;
    border: 1px solid #f0f0f2;
    border-radius: 18px;
    overflow: hidden;
  }
  /* page sans scroll : seul le tableau défile (même principe que DataList) */
  .yousign.page--fill > .table {
    flex: 0 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .table >>> .v-data-table {
    flex: 0 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .table >>> .v-data-table__wrapper {
    flex: 0 1 auto;
    min-height: 0;
    overflow: auto;
  }
  .table >>> th {
    background: white !important;
  }
  .table >>> .v-data-footer {
    flex: none;
  }
  .table >>> tbody tr {
    cursor: pointer;
  }
  .notice {
    display: flex;
    align-items: center;
    padding: 20px;
    color: #52525b;
  }
  .rate {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .rate__bar {
    flex: 1;
    height: 6px;
    border-radius: 3px;
    background: #f1f1f3;
    overflow: hidden;
  }
  .rate__bar div {
    height: 100%;
    background: #111113;
  }
  .rate span {
    width: 40px;
    text-align: right;
    font-size: 12.5px;
  }
</style>
